type Tokens = { accessToken: string; refreshToken: string };

// Стабильный ключ без привязки к git-commit, чтобы не выбивать из сессии после деплоя.
const STABLE_STORAGE_KEY = 'app_tokens';
const STABLE_MASTER_ID_KEY = 'app_master_id';

// Миграция с build-specific ключей (чтобы не потерять текущие токены при переходе на стабильный ключ)
const BUILD_ID = import.meta.env.VITE_GIT_COMMIT || 'dev';
const VERSIONED_STORAGE_KEY = `app_tokens_${BUILD_ID}`;
const VERSIONED_MASTER_ID_KEY = `app_master_id_${BUILD_ID}`;

// Backward-compat: старые ключи (до унификации)
const LEGACY_STORAGE_KEY = `tg_tokens_${BUILD_ID}`;
const LEGACY_MASTER_ID_KEY = `tg_master_id_${BUILD_ID}`;
let memoryTokens: Tokens | null = null;
let memoryMasterId: string | null = null;

/** Безопасный доступ к localStorage. При любой ошибке (quota, private mode) — null, используем in-memory. */
function getStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/** Вызов storage метода в try/catch; при ошибке — fallback на memory (сессия). */
function safeGetItem(storage: Storage, key: string): string | null {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function safeSetItem(storage: Storage, key: string, value: string): void {
  try {
    storage.setItem(key, value);
  } catch {
    /* fallback: пишем только в memory */
  }
}

function safeRemoveItem(storage: Storage, key: string): void {
  try {
    storage.removeItem(key);
  } catch {
    /* ignore */
  }
}

export function getAccessToken(): string | null {
  const storage = getStorage();
  if (storage) {
    try {
      const rawStable = safeGetItem(storage, STABLE_STORAGE_KEY);
      if (rawStable) {
        const parsed = JSON.parse(rawStable) as Tokens;
        return parsed.accessToken ?? null;
      }
      const raw = safeGetItem(storage, VERSIONED_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as Tokens;
        safeSetItem(storage, STABLE_STORAGE_KEY, JSON.stringify(parsed));
        return parsed.accessToken ?? null;
      }
      const legacyRaw = safeGetItem(storage, LEGACY_STORAGE_KEY);
      if (legacyRaw) {
        const parsed = JSON.parse(legacyRaw) as Tokens;
        safeSetItem(storage, STABLE_STORAGE_KEY, JSON.stringify(parsed));
        safeRemoveItem(storage, LEGACY_STORAGE_KEY);
        return parsed.accessToken ?? null;
      }
    } catch {
      /* malformed or storage threw → use memory */
    }
  }
  return memoryTokens?.accessToken ?? null;
}

export function getRefreshToken(): string | null {
  const storage = getStorage();
  if (storage) {
    try {
      const rawStable = safeGetItem(storage, STABLE_STORAGE_KEY);
      if (rawStable) {
        const parsed = JSON.parse(rawStable) as Tokens;
        return parsed.refreshToken ?? null;
      }
      const raw = safeGetItem(storage, VERSIONED_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as Tokens;
        safeSetItem(storage, STABLE_STORAGE_KEY, JSON.stringify(parsed));
        return parsed.refreshToken ?? null;
      }
      const legacyRaw = safeGetItem(storage, LEGACY_STORAGE_KEY);
      if (legacyRaw) {
        const parsed = JSON.parse(legacyRaw) as Tokens;
        safeSetItem(storage, STABLE_STORAGE_KEY, JSON.stringify(parsed));
        safeRemoveItem(storage, LEGACY_STORAGE_KEY);
        return parsed.refreshToken ?? null;
      }
    } catch {
      /* malformed or storage threw → use memory */
    }
  }
  return memoryTokens?.refreshToken ?? null;
}

export function setTokens(tokens: Tokens) {
  const storage = getStorage();
  if (storage) {
    safeSetItem(storage, STABLE_STORAGE_KEY, JSON.stringify(tokens));
    safeRemoveItem(storage, VERSIONED_STORAGE_KEY);
    safeRemoveItem(storage, LEGACY_STORAGE_KEY);
  }
  memoryTokens = tokens;
}

export function setMasterId(masterId: string | null): void {
  const storage = getStorage();
  if (storage) {
    try {
      if (masterId) {
        safeSetItem(storage, STABLE_MASTER_ID_KEY, masterId);
        safeRemoveItem(storage, VERSIONED_MASTER_ID_KEY);
        safeRemoveItem(storage, LEGACY_MASTER_ID_KEY);
      } else {
        safeRemoveItem(storage, STABLE_MASTER_ID_KEY);
        safeRemoveItem(storage, VERSIONED_MASTER_ID_KEY);
        safeRemoveItem(storage, LEGACY_MASTER_ID_KEY);
      }
    } catch {
      /* storage threw → only memory */
    }
  }
  memoryMasterId = masterId;
}

export function getMasterId(): string | null {
  const storage = getStorage();
  if (storage) {
    try {
      const stored = safeGetItem(storage, STABLE_MASTER_ID_KEY);
      if (stored) return stored;
      const versionedStored = safeGetItem(storage, VERSIONED_MASTER_ID_KEY);
      if (versionedStored) {
        safeSetItem(storage, STABLE_MASTER_ID_KEY, versionedStored);
        safeRemoveItem(storage, VERSIONED_MASTER_ID_KEY);
        return versionedStored;
      }
      const legacyStored = safeGetItem(storage, LEGACY_MASTER_ID_KEY);
      if (legacyStored) {
        safeSetItem(storage, STABLE_MASTER_ID_KEY, legacyStored);
        safeRemoveItem(storage, LEGACY_MASTER_ID_KEY);
        return legacyStored;
      }
    } catch {
      /* storage threw → use memory */
    }
  }
  return memoryMasterId;
}

export function clearTokens() {
  const storage = getStorage();
  if (storage) {
    try {
      safeRemoveItem(storage, STABLE_STORAGE_KEY);
      safeRemoveItem(storage, STABLE_MASTER_ID_KEY);
      safeRemoveItem(storage, VERSIONED_STORAGE_KEY);
      safeRemoveItem(storage, VERSIONED_MASTER_ID_KEY);
      safeRemoveItem(storage, LEGACY_STORAGE_KEY);
      safeRemoveItem(storage, LEGACY_MASTER_ID_KEY);
    } catch {
      /* ignore */
    }
  }
  memoryTokens = null;
  memoryMasterId = null;
  clearVkLaunchRawQuery();
  clearTgInitData();
}

// --- Platform context (для linked-accounts после email-логина)

const VK_RAW_QUERY_KEY = 'vk_raw_query';
const LEGACY_VK_RAW_QUERY_KEY = 'vk_launch_raw_query';
const TG_INIT_DATA_KEY = 'tg_init_data';
let memoryVkRawQuery: string | null = null;
let memoryTgInitData: string | null = null;

function getSessionStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/** rawQuery содержит sign? (обязательно для валидной привязки VK) */
export function vkRawQueryHasSign(rawQuery: string): boolean {
  try {
    const params = new URLSearchParams(rawQuery);
    return !!params.get('sign');
  } catch {
    return false;
  }
}

export function getVkLaunchRawQuery(): string | null {
  const storage = getSessionStorage();
  if (storage) {
    const v = safeGetItem(storage, VK_RAW_QUERY_KEY);
    if (v) return v;
    const legacy = safeGetItem(storage, LEGACY_VK_RAW_QUERY_KEY);
    if (legacy) return legacy;
  }
  return memoryVkRawQuery;
}

export function setVkLaunchRawQuery(rawQuery: string): void {
  if (!vkRawQueryHasSign(rawQuery)) return;
  const storage = getSessionStorage();
  if (storage) safeSetItem(storage, VK_RAW_QUERY_KEY, rawQuery);
  memoryVkRawQuery = rawQuery;
}

export function clearVkLaunchRawQuery(): void {
  const storage = getSessionStorage();
  if (storage) {
    safeRemoveItem(storage, VK_RAW_QUERY_KEY);
    safeRemoveItem(storage, LEGACY_VK_RAW_QUERY_KEY);
  }
  memoryVkRawQuery = null;
}

export function getTgInitData(): string | null {
  const storage = getSessionStorage();
  if (storage) {
    const v = safeGetItem(storage, TG_INIT_DATA_KEY);
    if (v) return v;
  }
  return memoryTgInitData;
}

export function setTgInitData(initData: string): void {
  if (!initData || !initData.trim()) return;
  const storage = getSessionStorage();
  if (storage) safeSetItem(storage, TG_INIT_DATA_KEY, initData);
  memoryTgInitData = initData;
}

export function clearTgInitData(): void {
  const storage = getSessionStorage();
  if (storage) safeRemoveItem(storage, TG_INIT_DATA_KEY);
  memoryTgInitData = null;
}
